import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import noop from 'utils/noop';
import { Trans } from 'services/i18n';
import { FolderId } from 'modules/domains';
import { CurrentSelection } from 'utils/contexts/selection';
import { useTranslation } from 'modules/domains/list';
import { renameListFolderMutation } from 'api/mutations';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { ResponseWrapper, NamedFolder } from 'ui/toast';

export enum KnownRenameFailures {
  CannotRenameRootFolderFailure = 'CannotRenameRootFolderFailure',
  FolderNameConflictFailure = 'FolderNameConflictFailure',
  InvalidFolderNameFailure = 'InvalidFolderNameFailure',
}

export type RenameError = {
  id: FolderId;
  name?: string;
  __typename: KnownRenameFailures;
};

type RenameFolderSuccess = {
  folderId: FolderId;
  folderName: string;
};

export type RenameListFolderResponseData = {
  renameListFolder: {
    success: RenameFolderSuccess;
    failure: RenameError;
  };
};

export type Variables = {
  folderId: FolderId;
  newName: string;
};

type ConfigProps = {
  folder: CurrentSelection;
  onComplete?: () => void;
  onError?: () => void;
  onSuccess: () => void;
};

export function useRenameListFolderMutation({
  folder,
  onComplete = noop,
  onError = noop,
  onSuccess = noop,
}: ConfigProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [renameFolder, { called, loading, error, data }] = useMutation<
    RenameListFolderResponseData,
    Variables
  >(renameListFolderMutation);

  useEffect(() => {
    if (!called) {
      return;
    }

    if (!loading && error) {
      dispatch(addNegativeToast(t('indexPage.toasts.renameListFolder.error.unknown')));
    } else if (data?.renameListFolder) {
      const { failure, success } = data.renameListFolder;

      if (failure) {
        switch (failure.__typename) {
          case KnownRenameFailures.CannotRenameRootFolderFailure: {
            dispatch(
              addNegativeToast(
                <ResponseWrapper>
                  {t('indexPage.toasts.renameListFolder.error.root')}
                </ResponseWrapper>,
              ),
            );
            break;
          }

          case KnownRenameFailures.FolderNameConflictFailure: {
            dispatch(
              addNegativeToast(
                <ResponseWrapper>
                  {t('indexPage.toasts.renameListFolder.error.nameConflict')}
                </ResponseWrapper>,
              ),
            );
            break;
          }

          case KnownRenameFailures.InvalidFolderNameFailure: {
            dispatch(
              addNegativeToast(
                <ResponseWrapper>
                  {t('indexPage.toasts.renameListFolder.error.invalidName')}
                </ResponseWrapper>,
              ),
            );
            break;
          }

          default: {
            dispatch(
              addNegativeToast(
                <ResponseWrapper>
                  <Trans
                    t={t}
                    i18nKey="indexPage.toasts.renameListFolder.error.default"
                    components={{
                      namedFolder: <NamedFolder name={folder.name} />,
                    }}
                  >
                    <NamedFolder name={folder.name} /> could not be renamed
                  </Trans>
                </ResponseWrapper>,
              ),
            );
          }
        }
        onError();
      } else if (success) {
        dispatch(
          addPositiveToast(
            <ResponseWrapper>
              <Trans
                t={t}
                i18nKey="indexPage.toasts.renameListFolder.success"
                components={{
                  namedFolder: <NamedFolder name={folder.name} />,
                  renamedFolder: <NamedFolder name={success.folderName} />,
                }}
              >
                <NamedFolder name={folder.name} /> was successfully renamed to{' '}
                <NamedFolder name={success.folderName} /> folder.
              </Trans>
            </ResponseWrapper>,
          ),
        );
        onSuccess();
      }
      onComplete();
    }
  }, [loading, error, called]);

  return [renameFolder, data] as const;
}
